import { isRacingCategoryName } from './racingCategory';

const ONCE_GUARD_MS = 800;
const onceTimestamps = new Map();

function omitEmpty(obj = {}) {
    const out = {};
    Object.entries(obj).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;
        if (typeof value === 'string' && value.toLowerCase() === 'all') return;
        out[key] = value;
    });
    return out;
}

const GENERIC_BROWSE_LABELS = new Set([
    'all',
    'all models',
    'all years',
    'all categories',
    'كل الموديلات',
    'كل السنوات',
    'كل الفئات',
]);

function isGenericBrowseLabel(value) {
    if (value == null) return true;
    const trimmed = String(value).trim();
    if (!trimmed) return true;
    return GENERIC_BROWSE_LABELS.has(trimmed.toLowerCase()) || GENERIC_BROWSE_LABELS.has(trimmed);
}

function readableName(value) {
    if (isGenericBrowseLabel(value)) return undefined;
    return String(value).trim();
}

/** Persist a localized name next to its id. Skips All / empty labels. Does not delete existing UI labels. */
export function persistBrowseName(key, name) {
    if (typeof window === 'undefined') return;
    const readable = readableName(name);
    if (!readable) return;
    localStorage.setItem(key, readable);
}

export function clearBrowseName(key) {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(key);
}

export function nameFromSelect(event) {
    const el = event?.target;
    if (!el || el.selectedIndex == null || !el.options) return '';
    return (el.options[el.selectedIndex]?.textContent || '').trim();
}

export function findNameById(list, id, nameKeys = ['name']) {
    if (id == null || String(id).toLowerCase() === 'all') return '';
    const item = (list || []).find((entry) => String(entry?.id) === String(id));
    if (!item) return '';
    for (const key of nameKeys) {
        if (item[key]) return String(item[key]);
    }
    return '';
}

export function bool01(value) {
    return value ? 1 : 0;
}

export function isFordBrand() {
    return process.env.REACT_APP_BRAND_ID === '1';
}

export function onceGuard(key, ttlMs = ONCE_GUARD_MS) {
    if (!key) return true;
    const now = Date.now();
    const previous = onceTimestamps.get(key);
    if (previous && now - previous < ttlMs) return false;
    onceTimestamps.set(key, now);
    return true;
}

export function getSharedContext() {
    if (typeof window === 'undefined') return {};
    return omitEmpty({
        country_id: localStorage.getItem('country_id'),
        country_name: readableName(localStorage.getItem('country_name')),
        dealer_id: localStorage.getItem('dealer_id'),
        dealer_name: readableName(localStorage.getItem('dealer_name')),
        language: localStorage.getItem('language') || 'en',
        brand_id: process.env.REACT_APP_BRAND_ID,
        market_id: process.env.REACT_APP_MARKET_ID,
    });
}

export function getBrowseContext(overrides = {}) {
    if (typeof window === 'undefined') {
        return omitEmpty({
            ...overrides,
            model_name: readableName(overrides.model_name),
            year_name: readableName(overrides.year_name),
            category_name: readableName(overrides.category_name),
            variant_name: readableName(overrides.variant_name),
        });
    }
    return omitEmpty({
        model_id: localStorage.getItem('selectedModel'),
        year_id: localStorage.getItem('selectedYear'),
        category_id: localStorage.getItem('selectedCategory'),
        ...overrides,
        model_name: readableName(overrides.model_name ?? localStorage.getItem('selectedModelName')),
        year_name: readableName(overrides.year_name ?? localStorage.getItem('selectedYearName')),
        category_name: readableName(overrides.category_name ?? localStorage.getItem('selectedCategoryName')),
        variant_name: readableName(overrides.variant_name),
    });
}

export function accessoryIsRacing(accessory) {
    if (!accessory) return 0;
    const cats = Array.isArray(accessory.categories) ? accessory.categories : [];
    if (cats.some((c) => isRacingCategoryName(c?.name))) return 1;
    if (isRacingCategoryName(accessory.item_category || accessory.category?.name)) return 1;
    if (accessory.is_racing) return 1;
    return 0;
}

export function fittedPrice(accessory) {
    const candidates = [
        accessory?.totalWithTax,
        accessory?.totalBeforeTax,
        accessory?.price,
    ];
    for (const candidate of candidates) {
        if (candidate == null || candidate === '') continue;
        const n = Number(candidate);
        if (!Number.isNaN(n)) return n;
    }
    return undefined;
}

export function buildEcommerceItem(accessory, extras = {}) {
    const browse = getBrowseContext({
        model_id: extras.model_id,
        model_name: extras.model_name,
        year_id: extras.year_id,
        year_name: extras.year_name,
        variant_name: extras.variant_name,
    });
    if (!accessory) {
        return omitEmpty({
            item_id: extras.item_id,
            accessory_id: extras.item_id,
            model_id: browse.model_id,
            model_name: browse.model_name,
            year_id: browse.year_id,
            year_name: browse.year_name,
            variant_name: browse.variant_name,
            ...extras,
        });
    }
    const itemId = accessory.id != null ? String(accessory.id) : extras.item_id;
    const price = fittedPrice(accessory);
    const categoryName = extras.item_category
        || accessory.item_category
        || accessory.category?.name
        || (Array.isArray(accessory.categories) && accessory.categories[0]?.name)
        || undefined;
    return omitEmpty({
        item_id: itemId,
        accessory_id: itemId,
        item_name: accessory.accessory_name || accessory.name || extras.item_name,
        item_category: categoryName,
        part_number: accessory.part_number,
        price,
        quantity: extras.quantity != null ? extras.quantity : 1,
        item_list_name: extras.item_list_name,
        is_racing: accessoryIsRacing(accessory),
        has_3d: accessory.has_3d ? 1 : 0,
        // Item-scoped vehicle context so cart/checkout lines stay tied to the selected model
        model_id: browse.model_id,
        model_name: browse.model_name,
        year_id: browse.year_id,
        year_name: browse.year_name,
        variant_name: browse.variant_name,
    });
}

export function mapAccessoriesToItems(accessories, itemListName, cap = 20) {
    if (!Array.isArray(accessories)) return [];
    return accessories.slice(0, cap).map((accessory, index) => omitEmpty({
        ...buildEcommerceItem(accessory, { item_list_name: itemListName, quantity: 1 }),
        index,
    }));
}

export function inferAddToCartSource() {
    if (typeof window === 'undefined') return 'pdp';
    const path = window.location.pathname || '';
    if (path.startsWith('/3d-viewer')) return '3d_viewer';
    if (path.startsWith('/cart')) return 'cart';
    return 'pdp';
}

export function shouldSkipPageView(pathname) {
    if (!pathname) return true;
    if (pathname.startsWith('/3d-viewer')) return true;
    if (pathname.startsWith('/dl/accessories')) return true;
    if (typeof window === 'undefined') return false;
    const countryId = localStorage.getItem('country_id');
    const dealerId = localStorage.getItem('dealer_id');
    if (!countryId && pathname !== '/country') return true;
    if (!dealerId && pathname !== '/country' && pathname !== '/dealer') return true;
    return false;
}

export function pushEvent(eventName, params = {}) {
    if (typeof window === 'undefined' || !eventName) return;
    window.dataLayer = window.dataLayer || [];
    const payload = {
        event: eventName,
        ...getSharedContext(),
        ...omitEmpty(params),
    };
    try {
        window.dataLayer.push(payload);
    } catch (_) {
        /* ignore */
    }
}

export function pushEcommerceEvent(eventName, { ecommerce, ...params } = {}) {
    if (typeof window === 'undefined' || !eventName) return;
    window.dataLayer = window.dataLayer || [];
    try {
        window.dataLayer.push({ ecommerce: null });
    } catch (_) {
        /* ignore */
    }
    const payload = {
        event: eventName,
        ...getSharedContext(),
        ...omitEmpty(params),
    };
    if (ecommerce) payload.ecommerce = ecommerce;
    try {
        window.dataLayer.push(payload);
    } catch (_) {
        /* ignore */
    }
}

export function trackSelectItem(accessory, itemListName, extraParams = {}) {
    const item = buildEcommerceItem(accessory, { item_list_name: itemListName, quantity: 1 });
    pushEcommerceEvent('select_item', {
        item_list_name: itemListName,
        is_racing: accessoryIsRacing(accessory),
        ...getBrowseContext(),
        ...omitEmpty(extraParams),
        ecommerce: {
            item_list_name: itemListName,
            items: [item],
        },
    });
}

export function trackViewItemList({
    accessories,
    itemListName,
    page = 1,
    browseOverrides = {},
    force = false,
    show3dOnly = false,
    showRacingOnly = false,
} = {}) {
    const browse = getBrowseContext(browseOverrides);
    const guardKey = `view_item_list:${itemListName}:${page}:${browse.model_id || ''}:${browse.year_id || ''}:${browse.category_id || ''}:3d${bool01(show3dOnly)}:race${bool01(showRacingOnly)}`;
    if (!force && !onceGuard(guardKey)) return;
    const items = mapAccessoriesToItems(accessories, itemListName, 20);
    pushEcommerceEvent('view_item_list', {
        item_list_name: itemListName,
        page,
        show_3d_only: bool01(show3dOnly),
        show_racing_only: bool01(showRacingOnly),
        ...browse,
        ecommerce: {
            item_list_name: itemListName,
            items,
        },
    });
}

export function trackSearchPerformed({
    resultCount,
    show3dOnly = false,
    showRacingOnly = false,
    racingCategoryId,
    formCategory,
    formModel,
    formYear,
    modelName,
    yearName,
    categoryName,
} = {}) {
    const categoryId = (showRacingOnly && racingCategoryId)
        ? racingCategoryId
        : (formCategory && formCategory !== 'all' && formCategory !== 'new' ? formCategory : undefined);
    pushEvent('site_search_performed', omitEmpty({
        ...getBrowseContext({
            model_id: formModel,
            year_id: formYear,
            category_id: categoryId,
            model_name: modelName,
            year_name: yearName,
            category_name: categoryName,
        }),
        show_3d_only: bool01(show3dOnly),
        show_racing_only: bool01(showRacingOnly),
        result_count: resultCount,
    }));
}

export function trackViewIn3dClicked(accessoryId, source) {
    if (!isFordBrand()) return;
    pushEvent('site_view_in_3d_clicked', {
        accessory_id: accessoryId,
        ui_source: source,
        ...getBrowseContext(),
    });
}

export function getItemListNameFromCategory(formCategory) {
    if (formCategory === 'new') return 'new';
    if (formCategory && formCategory !== 'all') return 'category';
    return 'search';
}
